// src/pages/products/Edit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../core/api';
import './Products.css';
import ProductsSkeleton from './ProductsSkeleton';
import { formatNumber, parseNum } from '../../utils/factorHelpers';

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const nameInputRef = useRef(null);

  const [form, setForm] = useState({
    code: '',
    name: '',
    unit: '',
    price: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  // ردیابی تغییرات ذخیره نشده
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  /* ============== Fetch ============== */
  useEffect(() => {
    let cancelled = false;

    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${id}`);
        const product = res.data?.product || res.data;

        if (!product || !product.id) {
          if (!cancelled) setNotFound(true);
          return;
        }

        if (!cancelled) {
          setForm({
            code: product.code || '',
            name: product.name || '',
            unit: product.unit || '',
            price: product.price ? String(Math.round(Number(product.price))) : '',
            description: product.description || '',
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('خطا در دریافت محصول:', err);

        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setMessage('خطا در دریافت اطلاعات محصول');
          setMessageType('danger');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* ============== Toast auto-hide ============== */
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  /* ============== beforeunload ============== */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /* ============== Handlers ============== */
  const markDirty = () => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      setIsDirty(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    markDirty();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: (form.description || '').trim(),
        unit: form.unit.trim(),
        price: Number(form.price) || 0,
      };

      await api.post(`/products/${id}/edit`, payload);

      // ریست dirty
      setIsDirty(false);
      isDirtyRef.current = false;

      setMessage('تغییرات با موفقیت ذخیره شد');
      setMessageType('success');

      // بازگشت به لیست بعد از ۱ ثانیه
      setTimeout(() => {
        navigate('/dashboard/products');
      }, 1000);
    } catch (err) {
      console.error('خطا در ذخیره تغییرات:', err);
      setMessage(
        err.response?.data?.error || 'خطا در ذخیره تغییرات'
      );
      setMessageType('danger');
    } finally {
      setSaving(false);
    }
  };

  /* ============== Render ============== */
  if (loading) {
    return <ProductsSkeleton />;
  }

  if (notFound) {
    return (
      <div className="card">
        <h2>محصول یافت نشد</h2>
        <p className="empty">
          محصولی با این شناسه وجود ندارد یا حذف شده است.
        </p>
        <div className="flexend">
          <Link className="btn secondary" to="/dashboard/products">
            <i className="fa-solid fa-arrow-right" /> بازگشت به لیست
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {message && (
        <div
          className={
            messageType === 'danger' ? 'toast-danger' : 'toast-success'
          }
        >
          {message}
        </div>
      )}

      <div className="card">
        <h2>ویرایش محصول</h2>

        {/* بنر تغییرات ذخیره نشده */}
        {isDirty && (
          <div className="dirty-banner">
            <i className="fa-solid fa-triangle-exclamation" />
            تغییرات ذخیره نشده دارید. لطفاً قبل از خروج ذخیره کنید.
          </div>
        )}

        <form onSubmit={handleSubmit} id="editProductForm">
          <div className="grid">
            <div>
              <label>کد کالا</label>
              <input
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="اختیاری"
              />
            </div>
            <div>
              <label>شرح کالا یا خدمات *</label>
              <input
                ref={nameInputRef}
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label>واحد اندازه‌گیری</label>
              <input
                name="unit"
                value={form.unit}
                onChange={handleChange}
                placeholder="عدد / کیلوگرم / متر ..."
              />
            </div>
            <div>
              <label>مبلغ واحد (ریال)</label>
              <input
                name="price"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={form.price ? formatNumber(parseNum(form.price)) : ''}
                onChange={(e) => {
                  const raw = parseNum(e.target.value);
                  setForm((prev) => ({ ...prev, price: raw ? String(raw) : '' }));
                  markDirty();
                }}
                placeholder="مثلاً 1,500,000"
              />
            </div>

            <div className="field-full">
              <label>توضیحات (اختیاری)</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="توضیحات دربارهٔ محصول..."
              />
            </div>
          </div>

          <div className="flexend">
            <Link
              className="btn secondary"
              to="/dashboard/products"
              style={{ marginLeft: 8 }}
            >
              <i className="fa-solid fa-xmark" /> انصراف
            </Link>
            <button className="btn" type="submit" disabled={saving}>
              {!saving && <i className="fa-solid fa-floppy-disk" />}
              <span className="btn-label">
                {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </span>
              {saving && (
                <span className="btn-spinner">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    style={{ animation: 'spin 0.8s linear infinite' }}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray="90 150"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Edit;
